import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search,
  Filter,
  Download,
  MoreHorizontal,
  Mail,
  FileEdit,
  Trash2,
  BookOpen,
  GraduationCap,
  User,
  BarChart,
  Award,
  ChevronDown,
  ChevronRight,
  FileText,
  Activity,
  ExternalLink
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { juniorSubjects, seniorSubjects } from "../../data/mockAssessments";
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { AddStudentDialog } from '@/components/students/AddStudentDialog';
import { ImportStudentsDialog } from '@/components/students/ImportStudentsDialog';
import { RemovedStudentsDialog } from '@/components/students/RemovedStudentsDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Upload } from 'lucide-react';

const STUDENT_ADMIN_ROLES = ['principal', 'academic_principal', 'deputy_principal', 'admin'];
// "Year 1" must not match "Year 10"
const gradeIs = (grade, label) => !!grade && new RegExp(`^${label}(?!\\d)`).test(grade);

export default function StudentsPage() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const { isFrench, t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGrade, setFilterGrade] = useState("All");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentGrades, setStudentGrades] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interventionDialogOpen, setInterventionDialogOpen] = useState(false);
  const [interventionStudent, setInterventionStudent] = useState(null);
  const [interventionNotes, setInterventionNotes] = useState("");
  const [interventions, setInterventions] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [removedOpen, setRemovedOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const canManage = STUDENT_ADMIN_ROLES.includes(currentUser?.role);
  const schoolYears = currentUser?.school_type === 'primary' ? [1, 2, 3, 4, 5, 6]
    : currentUser?.school_type === 'junior_secondary' ? [7, 8, 9, 10]
    : currentUser?.school_type === 'senior_secondary' ? [7, 8, 9, 10, 11, 12, 13]
    : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

  const reloadStudents = async () => {
    try {
      setStudents(await apiService.listStudents());
    } catch (e) {
      toast.error('Failed to refresh students');
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await apiService.removeStudent(removeTarget.id);
      setStudents((prev) => prev.filter((s) => s.id !== removeTarget.id));
      toast.success(`${removeTarget.name} removed from the directory`);
      setRemoveTarget(null);
    } catch (e) {
      toast.error(e.message || 'Failed to remove student');
    } finally {
      setRemoving(false);
    }
  };
  
  // Determine initial tab based on user permissions and school type
  const isComputerStudiesTeacher = currentUser?.restrictedTo === 'ICT';
  const shouldShowJunior = !currentUser || currentUser.role === 'admin' || currentUser.role === 'principal' || 
    (currentUser.role === 'teacher' && !isComputerStudiesTeacher);
  
  // Grade levels by school type
  const primaryGrades = ["Year 1", "Year 2", "Year 3", "Year 4", "Year 5", "Year 6"];
  const juniorGrades = ["Year 7", "Year 8", "Year 9", "Year 10"];
  const seniorGrades = ["Year 11", "Year 12", "Year 13"];
  
  // Determine default tab based on students loaded
  const [activeSection, setActiveSection] = useState("all");

  // Load interventions from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('student_interventions');
    if (saved) {
      setInterventions(JSON.parse(saved));
    }
  }, []);

  // Fetch students from backend
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const data = await apiService.listStudents();
        setStudents(data);
        
        // Auto-select the appropriate tab based on available students
        const hasPrimaryStudents = data.some(s => s.department?.match(/Year [1-6]\b/));
        const hasJuniorStudents = data.some(s => s.department?.match(/Year (7|8|9|10)\b/));
        const hasSeniorStudents = data.some(s => s.department?.match(/Year (11|12|13)\b/));
        
        if (hasPrimaryStudents) {
          setActiveSection("primary");
        } else if (hasJuniorStudents) {
          setActiveSection("junior");
        } else if (hasSeniorStudents) {
          setActiveSection("senior");
        }
      } catch (error) {
        console.error('Error fetching students:', error);
        toast.error('Failed to load students');
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const handleIntervention = (student) => {
    setInterventionStudent(student);
    setInterventionNotes("");
    setInterventionDialogOpen(true);
  };

  const saveIntervention = () => {
    if (!interventionNotes.trim()) {
      toast.error('Please enter intervention notes');
      return;
    }

    const intervention = {
      studentId: interventionStudent.id,
      studentName: interventionStudent.name,
      date: new Date().toISOString(),
      notes: interventionNotes,
      clearedBy: currentUser?.name || 'Academic Principal',
      status: 'cleared'
    };

    const updatedInterventions = {
      ...interventions,
      [interventionStudent.id]: intervention
    };

    setInterventions(updatedInterventions);
    localStorage.setItem('student_interventions', JSON.stringify(updatedInterventions));

    toast.success(`Intervention recorded for ${interventionStudent.name}. At-risk status cleared.`);
    setInterventionDialogOpen(false);
    setInterventionStudent(null);
    setInterventionNotes("");
  };

  // Fetch student grades when a student is selected
  useEffect(() => {
    const fetchStudentGrades = async () => {
      if (selectedStudent && selectedStudent.id) {
        try {
          const grades = await apiService.getStudentGrades(selectedStudent.id);
          setStudentGrades(grades);
        } catch (error) {
          console.error('Error fetching student grades:', error);
          setStudentGrades([]);
        }
      }
    };

    if (sheetOpen && selectedStudent) {
      fetchStudentGrades();
    }
  }, [selectedStudent, sheetOpen]);

  // Convert backend student format to match frontend expectations
  const formatStudents = (backendStudents) => {
    return backendStudents.map(s => ({
      id: s.id,
      name: s.name,
      email: s.email,
      studentNumber: s.student_number,
      dateOfBirth: s.date_of_birth,
      parentName: s.parent_name,
      parentPhone: s.parent_phone,
      grade: s.department, // e.g., "Year 10A" or "Year 10"
      attendance: {
        present: 92,
        absent: 3,
        total: 95
      },
      gpa: 3.2,
      status: s.status
    }));
  };

  const formattedStudents = formatStudents(students);

  // Filter students based on user permissions
  const filterStudentsByUser = (students) => {
    // Admin and Principal can see all students
    if (!currentUser || currentUser.role === 'admin' || currentUser.role === 'principal' || currentUser.classes === 'all') {
      return students;
    }
    
    // For restricted teachers (like Roy Tari with ICT - senior classes only)
    if (currentUser.restrictedTo === 'ICT') {
      // Only show students from Year 11, 12, 13 for ICT teacher
      return students.filter(s => {
        const year = s.grade.match(/Year (\d+)/)?.[1];
        return year && ['11', '12', '13'].includes(year);
      });
    }
    
    return students;
  };

  const primaryStudents = filterStudentsByUser(formattedStudents.filter(s =>
    primaryGrades.some(grade => gradeIs(s.grade, grade))
  ));

  const juniorStudents = filterStudentsByUser(formattedStudents.filter(s =>
    juniorGrades.some(grade => gradeIs(s.grade, grade))
  ));

  const seniorStudents = filterStudentsByUser(formattedStudents.filter(s =>
    seniorGrades.some(grade => gradeIs(s.grade, grade))
  ));
  
  // Determine which tabs to show based on available students
  const hasPrimary = primaryStudents.length > 0;
  const hasJunior = juniorStudents.length > 0;
  const hasSenior = seniorStudents.length > 0;

  const filteredPrimary = primaryStudents.filter(
    (s) =>
      (filterGrade === "All" || gradeIs(s.grade, filterGrade)) &&
      s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredJunior = juniorStudents.filter(
    (s) =>
      (filterGrade === "All" || gradeIs(s.grade, filterGrade)) &&
      s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSenior = seniorStudents.filter(
    (s) =>
      (filterGrade === "All" || gradeIs(s.grade, filterGrade)) &&
      s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewDetails = (student) => {
    setSelectedStudent(student);
    setSheetOpen(true);
  };

  const getYearNumeric = (grade) => {
    const match = grade.match(/(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };

  const levelSubjects = selectedStudent && (juniorGrades.some(g => selectedStudent.grade.includes(g)) ? juniorSubjects : seniorSubjects);

  const generateMockGrades = (subjects) => {
    return subjects.map(sub => {
      const mockFinal = 60 + Math.random() * 30;
      return {
        subject: sub,
        ca: Math.round(mockFinal * 0.4),
        exam: Math.round(mockFinal * 0.6),
        total: Math.round(mockFinal),
        grade: mockFinal >= 80 ? "A" : mockFinal >= 70 ? "B" : mockFinal >= 60 ? "C" : "D"
      };
    });
  };

  const mockGrades = selectedStudent ? generateMockGrades(levelSubjects) : [];

  const StudentCard = ({ student }) => {
    const attendanceRate = ((student.attendance.present / student.attendance.total) * 100).toFixed(1);
    const isAtRisk = attendanceRate < 85 || student.attendance.absent >= 4;

    return (
      <div
        className="border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer group bg-gradient-to-br from-white to-slate-50"
        onClick={() => handleViewDetails(student)}
      >
        <div className="flex items-start gap-4">
          <Avatar className="h-12 w-12">
            <AvatarImage src={`https://randomuser.me/api/portraits/${student.avatar % 2 === 0 ? 'men' : 'women'}/${student.avatar}.jpg`} />
            <AvatarFallback>{student.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-bold text-base group-hover:text-primary transition-colors">
                  {student.name}
                </h3>
                <p className="text-xs text-muted-foreground">{student.grade}</p>
              </div>
              {isAtRisk && (
                <Badge variant="destructive" className="text-xs">{isFrench ? "À Risque" : "At Risk"}</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
              <div className="flex items-center gap-1">
                <Activity className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Attendance:</span>
                <span className="font-semibold">{attendanceRate}%</span>
              </div>
              <div className="flex items-center gap-1">
                <Award className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">GPA:</span>
                <span className="font-semibold">3.5</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StudentTableByYear = ({ students, years }) => {
    // Group students by year and stream
    const groupedStudents = {};
    years.forEach(year => {
      const yearStudents = students.filter(s => gradeIs(s.grade, year));
      
      // Further group by stream (Arts, Science, etc.)
      const byStream = {};
      yearStudents.forEach(student => {
        const stream = student.grade.replace(/Year \d+\s*/, '').trim() || 'General';
        if (!byStream[stream]) {
          byStream[stream] = [];
        }
        byStream[stream].push(student);
      });
      
      groupedStudents[year] = byStream;
    });

    const getStreamColor = (stream) => {
      switch(stream) {
        case 'Arts': return 'from-purple-50 to-purple-100 border-purple-200';
        case 'Science': return 'from-blue-50 to-blue-100 border-blue-200';
        case 'Commerce': return 'from-green-50 to-green-100 border-green-200';
        default: return 'from-slate-50 to-slate-100 border-slate-200';
      }
    };

    const getStreamBadgeColor = (stream) => {
      switch(stream) {
        case 'Arts': return 'bg-purple-100 text-purple-700 border-purple-300';
        case 'Science': return 'bg-blue-100 text-blue-700 border-blue-300';
        case 'Commerce': return 'bg-green-100 text-green-700 border-green-300';
        default: return 'bg-slate-100 text-slate-700 border-slate-300';
      }
    };

    return (
      <Accordion type="multiple" className="space-y-4" defaultValue={years}>
        {years.map(year => {
          const streamGroups = groupedStudents[year];
          const totalYearStudents = Object.values(streamGroups).reduce((sum, arr) => sum + arr.length, 0);
          
          if (totalYearStudents === 0) return null;

          return (
            <AccordionItem key={year} value={year} className="border rounded-lg overflow-hidden shadow-sm">
              <AccordionTrigger className="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 hover:no-underline">
                <div className="flex items-center gap-3 w-full">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <GraduationCap className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-bold text-lg">{year}</h3>
                    <p className="text-xs text-muted-foreground">
                      {totalYearStudents} student{totalYearStudents !== 1 ? 's' : ''} 
                      {Object.keys(streamGroups).length > 1 && ` · ${Object.keys(streamGroups).length} streams`}
                    </p>
                  </div>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    {totalYearStudents}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-0">
                <div className="space-y-4 p-4">
                  {Object.entries(streamGroups).sort(([a], [b]) => a.localeCompare(b)).map(([stream, streamStudents]) => (
                    <div key={`${year}-${stream}`} className={`rounded-lg border-2 overflow-hidden bg-gradient-to-br ${getStreamColor(stream)}`}>
                      <div className="px-4 py-3 border-b flex items-center justify-between">
                        <h4 className="font-bold text-sm flex items-center gap-2">
                          <span className="text-base">{stream} Stream</span>
                          <Badge variant="outline" className={`${getStreamBadgeColor(stream)}`}>
                            {streamStudents.length} students
                          </Badge>
                        </h4>
                      </div>
                      <div className="bg-white">
                        <Table>
                          <TableHeader className="bg-muted/30">
                            <TableRow>
                              <TableHead className="font-bold">Student Name</TableHead>
                              <TableHead className="font-bold text-center">Attendance</TableHead>
                              <TableHead className="font-bold text-center">Status</TableHead>
                              <TableHead className="text-right font-bold">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {streamStudents.map(student => {
                              const attendanceRate = ((student.attendance.present / student.attendance.total) * 100).toFixed(1);
                              const isAtRisk = attendanceRate < 85 || student.attendance.absent >= 4;

                              return (
                                <TableRow key={student.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewDetails(student)}>
                                  <TableCell>
                                    <div className="flex items-center gap-3">
                                      <Avatar className="h-9 w-9">
                                        <AvatarImage src={`https://randomuser.me/api/portraits/${student.avatar % 2 === 0 ? 'men' : 'women'}/${student.avatar}.jpg`} />
                                        <AvatarFallback>{student.name.charAt(0)}</AvatarFallback>
                                      </Avatar>
                                      <div>
                                        <div className="font-medium">{student.name}</div>
                                        <div className="text-xs text-muted-foreground">
                                          {student.studentNumber || student.dateOfBirth || student.parentPhone
                                            ? [student.studentNumber && `ID ${student.studentNumber}`, student.dateOfBirth && `DoB ${student.dateOfBirth}`, student.parentPhone && `Parent ${student.parentPhone}`].filter(Boolean).join(' · ')
                                            : (student.email || 'No email')}
                                        </div>
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <div className="flex flex-col items-center gap-1">
                                      <span className={`font-semibold ${attendanceRate >= 90 ? 'text-green-600' : attendanceRate >= 75 ? 'text-orange-600' : 'text-red-600'}`}>
                                        {attendanceRate}%
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {student.attendance.present}/{student.attendance.total}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {isAtRisk ? (
                                      <Badge variant="destructive" className="text-xs">At Risk</Badge>
                                    ) : (
                                      <Badge variant="default" className="bg-green-500 text-xs">Good</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                                          <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewDetails(student); }}>
                                          <User className="h-4 w-4 mr-2" /> View Profile
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                                          <Mail className="h-4 w-4 mr-2" /> Send Email
                                        </DropdownMenuItem>
                                        {canManage ? (
                                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditTarget(student); }} data-testid={`edit-student-${student.id}`}>
                                            <FileEdit className="h-4 w-4 mr-2" /> Edit Student
                                          </DropdownMenuItem>
                                        ) : (
                                          <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                                            <FileEdit className="h-4 w-4 mr-2" /> Edit Student
                                          </DropdownMenuItem>
                                        )}
                                        {canManage && (
                                          <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); setRemoveTarget(student); }} data-testid={`remove-student-${student.id}`}>
                                            <Trash2 className="h-4 w-4 mr-2" /> Remove
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };

  const StudentTable = ({ students, level }) => (
    <div className="rounded-md border overflow-hidden shadow-md">
      <Table>
        <TableHeader className="bg-gradient-to-r from-slate-100 to-slate-50">
          <TableRow>
            <TableHead className="font-bold">{isFrench ? "Nom de l'Élève" : "Student Name"}</TableHead>
            <TableHead className="font-bold">{isFrench ? "Classe" : "Grade"}</TableHead>
            <TableHead className="font-bold text-center">{isFrench ? "Présence" : "Attendance"}</TableHead>
            <TableHead className="font-bold text-center">{isFrench ? "Statut" : "Status"}</TableHead>
            <TableHead className="text-right font-bold">{isFrench ? "Actions" : "Actions"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map((student) => {
            const attendanceRate = ((student.attendance.present / student.attendance.total) * 100).toFixed(1);
            const hasIntervention = interventions[student.id]?.status === 'cleared';
            const isAtRisk = !hasIntervention && (attendanceRate < 85 || student.attendance.absent >= 4);

            return (
              <TableRow key={student.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={`https://randomuser.me/api/portraits/${student.avatar % 2 === 0 ? 'men' : 'women'}/${student.avatar}.jpg`} />
                      <AvatarFallback>{student.name.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{student.name}</span>
                  </div>
                </TableCell>
                <TableCell>{student.grade}</TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className={`${
                      attendanceRate >= 90
                        ? "bg-green-50 text-green-700 border-green-200"
                        : attendanceRate >= 80
                        ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    {attendanceRate}%
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  {isAtRisk ? (
                    <Badge variant="destructive">{isFrench ? "À Risque" : "At Risk"}</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">{isFrench ? "Bien" : "Good"}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewDetails(student)}>
                        <FileText className="h-4 w-4 mr-2" /> View Academic Record
                      </DropdownMenuItem>
                      {isAtRisk && (
                        <DropdownMenuItem onClick={() => handleIntervention(student)} className="text-blue-600">
                          <User className="h-4 w-4 mr-2" /> Record Intervention Meeting
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem>
                        <FileEdit className="h-4 w-4 mr-2" /> Edit Student
                      </DropdownMenuItem>
                      {canManage && (
                        <DropdownMenuItem className="text-red-600" onClick={() => setRemoveTarget(student)} data-testid={`remove-student-card-${student.id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Remove
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 -m-6 p-6">
      <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isFrench ? "Répertoire des Élèves" : "Student Directory"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isFrench ? "Gérer les dossiers des élèves de votre école." : "Manage student records for your school."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (
            <>
              <Button variant="outline" className="gap-2" onClick={() => setImportOpen(true)} data-testid="upload-students-btn">
                <Upload className="h-4 w-4" />
                {isFrench ? "Importer des élèves" : "Upload Students"}
              </Button>
              <Button className="gap-2 bg-teal-700 hover:bg-teal-800" onClick={() => setAddOpen(true)} data-testid="add-student-btn">
                <UserPlus className="h-4 w-4" />
                {isFrench ? "Ajouter un élève" : "Add Student"}
              </Button>
              <Button variant="ghost" className="gap-2 text-muted-foreground" onClick={() => setRemovedOpen(true)} data-testid="removed-students-btn">
                <Trash2 className="h-4 w-4" />
                {isFrench ? "Élèves retirés" : "Removed"}
              </Button>
            </>
          )}
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            {isFrench ? "Exporter CSV" : "Export CSV"}
          </Button>
          <Button className="gap-2">
            <Mail className="h-4 w-4" />
            {isFrench ? "Envoyer une Annonce" : "Send Announcement"}
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={isFrench ? "Rechercher un élève par nom..." : "Search students by name..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filterGrade}
          onChange={(e) => setFilterGrade(e.target.value)}
          className="border rounded-md px-4 py-2 bg-background"
        >
          <option value="All">{isFrench ? "Tous les Niveaux" : "All Grades"}</option>
          {[...primaryGrades, ...juniorGrades, ...seniorGrades].map(g => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <Tabs value={activeSection} onValueChange={setActiveSection}>
          <TabsList className="grid w-full max-w-2xl" style={{ gridTemplateColumns: `repeat(${(hasPrimary ? 1 : 0) + (hasJunior ? 1 : 0) + (hasSenior ? 1 : 0) || 1}, 1fr)` }}>
            {hasPrimary && (
              <TabsTrigger value="primary" className="gap-2">
                <BookOpen className="h-4 w-4" />
                {isFrench ? "Primaire (1-6)" : "Primary (Yr 1-6)"}
                <Badge variant="secondary">{filteredPrimary.length}</Badge>
              </TabsTrigger>
            )}
            {hasJunior && (
              <TabsTrigger value="junior" className="gap-2">
                <BookOpen className="h-4 w-4" />
                {isFrench ? "Collège (7e-10e)" : "Junior (Yr 7-10)"}
                <Badge variant="secondary">{filteredJunior.length}</Badge>
              </TabsTrigger>
            )}
            {hasSenior && (
              <TabsTrigger value="senior" className="gap-2">
                <GraduationCap className="h-4 w-4" />
                {isFrench ? "Lycée (11-13)" : "Senior (Yr 11-13)"}
                <Badge variant="secondary">{filteredSenior.length}</Badge>
              </TabsTrigger>
            )}
          </TabsList>

        {hasPrimary && (
          <TabsContent value="primary" className="mt-6">
            <StudentTableByYear students={filteredPrimary} years={primaryGrades} />
          </TabsContent>
        )}

        {hasJunior && (
          <TabsContent value="junior" className="mt-6">
            <StudentTableByYear students={filteredJunior} years={juniorGrades} />
          </TabsContent>
        )}

        <TabsContent value="senior" className="mt-6">
          {currentUser?.restrictedTo === 'computer-studies' ? (
            <div className="space-y-6">
              {/* Year 11 ICT */}
              <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-purple-600" />
                    Year 11 ICT
                    <Badge variant="secondary" className="bg-purple-200 text-purple-800">
                      {filteredSenior.filter(s => s.grade.includes('Year 11')).length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <StudentTable students={filteredSenior.filter(s => s.grade.includes('Year 11'))} level="senior" />
                </CardContent>
              </Card>

              {/* Year 12 ICT */}
              <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-blue-600" />
                    Year 12 ICT
                    <Badge variant="secondary" className="bg-blue-200 text-blue-800">
                      {filteredSenior.filter(s => s.grade.includes('Year 12')).length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <StudentTable students={filteredSenior.filter(s => s.grade.includes('Year 12'))} level="senior" />
                </CardContent>
              </Card>

              {/* Year 13 ICT */}
              <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-green-600" />
                    Year 13 ICT
                    <Badge variant="secondary" className="bg-green-200 text-green-800">
                      {filteredSenior.filter(s => s.grade.includes('Year 13')).length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <StudentTable students={filteredSenior.filter(s => s.grade.includes('Year 13'))} level="senior" />
                </CardContent>
              </Card>
            </div>
          ) : (
            <StudentTableByYear students={filteredSenior} years={seniorGrades} />
          )}
        </TabsContent>
      </Tabs>
      )}

      {/* Student Details Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[600px] sm:w-[700px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3">
              {selectedStudent && (
                <>
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={`https://randomuser.me/api/portraits/${selectedStudent.avatar % 2 === 0 ? 'men' : 'women'}/${selectedStudent.avatar}.jpg`} />
                    <AvatarFallback>{selectedStudent.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-xl font-bold">{selectedStudent.name}</p>
                    <p className="text-sm text-muted-foreground font-normal">{selectedStudent.grade}</p>
                  </div>
                </>
              )}
            </SheetTitle>
            <SheetDescription>
              Complete Academic Profile & Performance Record
            </SheetDescription>
          </SheetHeader>

          {selectedStudent && (
            <div className="space-y-6 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Attendance Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-green-600">{selectedStudent.attendance.present}</p>
                      <p className="text-xs text-muted-foreground">Present</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-600">{selectedStudent.attendance.absent}</p>
                      <p className="text-xs text-muted-foreground">Absent</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-orange-600">{selectedStudent.attendance.late}</p>
                      <p className="text-xs text-muted-foreground">Late</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-md text-center">
                    <p className="text-sm text-muted-foreground">Overall Attendance Rate</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {((selectedStudent.attendance.present / selectedStudent.attendance.total) * 100).toFixed(1)}%
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">Academic Performance</CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => {
                      setSheetOpen(false);
                      navigate(`/dashboard/gradebook?student=${selectedStudent.id}`);
                    }}
                    className="gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View in Grade Book
                  </Button>
                </CardHeader>
                <CardContent>
                  {studentGrades.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader className="bg-gradient-to-r from-blue-100 to-indigo-100">
                          <TableRow>
                            <TableHead className="font-bold">Assessment</TableHead>
                            <TableHead className="text-center font-bold">Raw Mark</TableHead>
                            <TableHead className="text-center font-bold">Max Mark</TableHead>
                            <TableHead className="text-center font-bold">Percentage</TableHead>
                            <TableHead className="text-center font-bold">Grade</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {studentGrades.map((g, i) => {
                            const grade = g.percentage >= 80 ? "A" : g.percentage >= 70 ? "B" : g.percentage >= 60 ? "C" : g.percentage >= 50 ? "D" : "F";
                            return (
                              <TableRow key={i} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50">
                                <TableCell className="font-medium text-xs">{g.assessment_id}</TableCell>
                                <TableCell className="text-center">{g.raw_marks}</TableCell>
                                <TableCell className="text-center">{g.max_marks}</TableCell>
                                <TableCell className="text-center font-bold">{g.percentage.toFixed(1)}%</TableCell>
                                <TableCell className="text-center">
                                  <Badge
                                    variant="outline"
                                    className={`${
                                      grade === "A" ? "bg-green-50 text-green-700 border-green-200" :
                                      grade === "B" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                      grade === "C" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                                      "bg-red-50 text-red-700 border-red-200"
                                    }`}
                                  >
                                    {grade}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No grades available yet</p>
                      <Button 
                        size="sm" 
                        variant="link"
                        onClick={() => {
                          setSheetOpen(false);
                          navigate(`/dashboard/gradebook?student=${selectedStudent.id}`);
                        }}
                      >
                        Go to Grade Book to add grades
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Intervention Meeting Dialog */}
      <AddStudentDialog open={addOpen} onOpenChange={setAddOpen} years={schoolYears} onCreated={reloadStudents} />
      <AddStudentDialog open={!!editTarget} onOpenChange={(v) => { if (!v) setEditTarget(null); }} years={schoolYears} onCreated={reloadStudents} student={editTarget} />
      <ImportStudentsDialog open={importOpen} onOpenChange={setImportOpen} years={schoolYears} onImported={reloadStudents} />
      <RemovedStudentsDialog open={removedOpen} onOpenChange={setRemovedOpen} onRestored={reloadStudents} />

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => { if (!v) setRemoveTarget(null); }}>
        <AlertDialogContent data-testid="remove-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the student from your school directory, class lists and roll calls. Their past grades are kept. You can ask support to restore them if this was a mistake.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-student-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmRemove(); }} disabled={removing} className="bg-red-600 hover:bg-red-700" data-testid="remove-student-confirm">
              {removing ? 'Removing…' : 'Remove Student'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={interventionDialogOpen} onOpenChange={setInterventionDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Record Intervention Meeting
            </DialogTitle>
            <DialogDescription>
              Document the meeting with {interventionStudent?.name} and Academic Principal
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Student Name</label>
                  <p className="text-sm font-semibold text-gray-900">{interventionStudent?.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Year Level</label>
                  <p className="text-sm font-semibold text-gray-900">{interventionStudent?.year}</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Meeting Date</label>
              <p className="text-sm text-gray-900">{new Date().toLocaleDateString()}</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Intervention Notes *</label>
              <Textarea
                placeholder="Enter details about the meeting, agreed actions, and next steps..."
                value={interventionNotes}
                onChange={(e) => setInterventionNotes(e.target.value)}
                rows={6}
                className="resize-none"
              />
              <p className="text-xs text-gray-500">
                Include: Issues discussed, student commitments, follow-up date, support provided
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Saving this intervention will clear the "At Risk" status for this student.
                The status will update based on future performance and attendance.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInterventionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveIntervention} className="bg-blue-600 hover:bg-blue-700">
              <User className="h-4 w-4 mr-2" />
              Save Intervention
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
